/**
 * Contrast maths, used as a guard rather than a utility.
 *
 * Authorised by: design-direction §3 ("text contrast is fixed at accessible levels"),
 * implementation-brief §11 ("WCAG-conformant text contrast at every Intensity setting").
 *
 * This exists so that the accessibility gate is a runnable assertion over the token
 * tables, not a promise in a document.
 */

export function relativeLuminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export const WCAG = {
  /** Normal-size body text. */
  AA_TEXT: 4.5,
  /** Text ≥ 24px, or ≥ 19px bold. */
  AA_LARGE_TEXT: 3,
  /** UI component boundaries and graphical objects (WCAG 1.4.11). */
  AA_NON_TEXT: 3,
  AAA_TEXT: 7,
} as const;

export function meetsAA(foreground: string, background: string, large = false): boolean {
  return contrastRatio(foreground, background) >= (large ? WCAG.AA_LARGE_TEXT : WCAG.AA_TEXT);
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new Error(`Not a hex colour: ${hex}`);
  }
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}
